import type { Task } from '@/types/task';
import { TaskCategory } from '@/types/task';
import { deriveSingleStatus } from '@/utils/prep-filter-utils';

/**
 * Computes the initial expand-state for each category group in the Prep task list.
 *
 * Rules (DASH-TD1 §3):
 * - All-DONE groups → collapsed by default.
 * - All other groups → expanded by default.
 * - Exactly ONE group is the "most urgent" and is always force-expanded:
 *     the group with the most OVERDUE tasks.
 *     Tiebreak: the group containing the soonest upcoming dueDate
 *     (soonest among non-done tasks with a future dueDate).
 *
 * This function is pure — no side effects, no storage calls.
 * Call it ONCE at page mount (seed-once pattern mirroring hasSeededRef at TaskListPage.tsx:60).
 */
export function computeInitialExpandStates(
  groupedTasks: Map<TaskCategory, Task[]>,
  todayStr: string,
): Map<TaskCategory, boolean> {
  const result = new Map<TaskCategory, boolean>();

  // Step 1: determine all-DONE state for each group
  const allDone = new Map<TaskCategory, boolean>();
  for (const [category, tasks] of groupedTasks) {
    const isDone = tasks.every((t) => deriveSingleStatus(t, todayStr) === 'done');
    allDone.set(category, isDone);
  }

  // Step 2: find the "most urgent" group
  let mostUrgentCategory: TaskCategory | null = null;
  let maxOverdue = -1;
  let soonestDueDate: string | null = null;

  for (const [category, tasks] of groupedTasks) {
    const overdueCount = tasks.filter((t) => deriveSingleStatus(t, todayStr) === 'overdue').length;
    const futureDueTasks = tasks
      .filter((t) => {
        const status = deriveSingleStatus(t, todayStr);
        return status !== 'done' && t.dueDate >= todayStr;
      })
      .map((t) => t.dueDate)
      .sort();

    const groupSoonestDue = futureDueTasks.length > 0 ? futureDueTasks[0] : null;

    if (overdueCount > maxOverdue) {
      maxOverdue = overdueCount;
      mostUrgentCategory = category;
      soonestDueDate = groupSoonestDue;
    } else if (overdueCount === maxOverdue) {
      // Tiebreak: soonest upcoming dueDate among non-done tasks
      if (groupSoonestDue !== null) {
        if (soonestDueDate === null || groupSoonestDue < soonestDueDate) {
          soonestDueDate = groupSoonestDue;
          mostUrgentCategory = category;
        }
      }
    }
  }

  // Step 3: assign expand states
  for (const [category] of groupedTasks) {
    if (category === mostUrgentCategory) {
      // Most urgent group is always force-expanded
      result.set(category, true);
    } else if (allDone.get(category) === true) {
      // All-DONE groups collapse
      result.set(category, false);
    } else {
      // All other groups are expanded
      result.set(category, true);
    }
  }

  return result;
}
